import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Semantic tone — drives the icon color and its tinted badge background. */
export type InsightTone = "success" | "error" | "neutral" | "accent";

/** A single labeled statistic shown beneath the header. */
export interface InsightStat {
  /** Uppercase caption, e.g. "Early Mean", "Current", "Total Sessions". */
  label: string;
  /** Formatted value, e.g. "75.8", "80.0", "24". */
  value: string;
}

export interface TargetInsightCardProps {
  /** Phosphor icon element rendered in the tinted badge — inherits the tone color. */
  icon: React.ReactNode;
  /** Semantic tone for the badge (color + background). */
  tone: InsightTone;
  /** Headline, e.g. "Improving", "Mastered", or a value like "22". */
  title: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  /** Two summary stats rendered side by side. */
  stats: InsightStat[];
  /**
   * Header layout. `"row"` puts the icon left of the title (trend direction
   * cards); `"column"` centers the icon above the title (mastery / criterion
   * cards). Defaults to `"row"`.
   */
  layout?: "row" | "column";
  /** Additional MUI sx overrides. */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Tone styles
// ---------------------------------------------------------------------------

const TONE_STYLES: Record<InsightTone, { bg: string; color: string }> = {
  success: { bg: "success.background", color: "success.main" },
  error: { bg: "error.background", color: "error.main" },
  neutral: { bg: "grayscale.50", color: "text.secondary" },
  accent: { bg: "purple.100", color: "purple.600" },
};

// ---------------------------------------------------------------------------
// TargetInsightCard
// ---------------------------------------------------------------------------

/**
 * Presentational insight card for the target detail view. Shows a tinted icon
 * badge, a headline + optional subtitle, and two labeled stats. Used for the
 * trend-direction (Improving / Declining / Stable), mastery, and
 * chain-completion summaries. All values are pre-computed by the caller.
 *
 * @param props
 * @param props.icon - Phosphor icon element (inherits the tone color)
 * @param props.tone - Semantic tone for the badge
 * @param props.title - Headline text
 * @param [props.subtitle] - Supporting line under the title
 * @param props.stats - Two labeled stats
 * @param [props.layout="row"] - Header layout
 * @param [props.sx] - Additional MUI sx overrides
 */
const TargetInsightCard: React.FC<TargetInsightCardProps> = ({
  icon,
  tone,
  title,
  subtitle,
  stats,
  layout = "row",
  sx,
}) => {
  const isColumn = layout === "column";
  const toneStyle = TONE_STYLES[tone];

  const badge = (
    <Box
      aria-hidden
      sx={{
        display: "inline-flex",
        p: 3,
        borderRadius: "50%",
        bgcolor: toneStyle.bg,
        color: toneStyle.color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
  );

  const info = (
    <Stack gap={0} sx={{ alignItems: isColumn ? "center" : "flex-start" }}>
      <Typography
        component="p"
        variant="h2"
        sx={{ fontWeight: 500, color: "text.primary", textAlign: isColumn ? "center" : "left" }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", textAlign: isColumn ? "center" : "left" }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );

  const statistics = (
    <Stack
      component="dl"
      direction="row"
      gap={6}
      sx={{ m: 0, justifyContent: isColumn ? "center" : "flex-start" }}
    >
      {stats.map((stat) => (
        <Stack key={stat.label} gap={0} sx={{ alignItems: isColumn ? "center" : "flex-start" }}>
          <Typography
            component="dt"
            variant="overline"
            sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "1px" }}
          >
            {stat.label}
          </Typography>
          <Typography component="dd" variant="h4" sx={{ m: 0, color: "text.primary" }}>
            {stat.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Box
      component="section"
      aria-label={title}
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 5,
          alignItems: isColumn ? "center" : "stretch",
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 7,
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {isColumn ? (
        <>
          <Stack alignItems="center" gap={3}>
            {badge}
            {info}
          </Stack>
          {statistics}
        </>
      ) : (
        <Stack direction="row" gap={6} alignItems="flex-start">
          {badge}
          <Stack gap={5} sx={{ flex: 1, minWidth: 0 }}>
            {info}
            {statistics}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default TargetInsightCard;
